/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState } from "react";
import Page from "components/common_components/Page";
import FormsContext from "./FormsContext";
import { useHistory, useParams } from "react-router-dom";
import Button from "components/common_components/Button";
import { useDispatch, useSelector } from "react-redux";
import { notifyError } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Status from "components/common_components/Status";
import Icon from "components/common_components/Icon";
import { ConfirmDialog } from "components/common_components/Dialogs";
import PdfForm from "./components/PdfForm";
import { getFormattedValue } from "./utils";
import FileCaseLink from "./components/FileCaseLink";
import useFormPreloaders from "./hooks/useFormPreloaders";
import { hasPermission } from "utils/session";
import { COPY_TO_CURRENT_VERSION_PERMISSION, findCurrentVersion } from "./utils/copyToVersion";

const ViewForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [formClient, setFormClient] = useState(undefined);
    const { FormClients, resources, getAttorneyUsersOptions, getFileUrlAsync, pdfUrl } = useContext(FormsContext);
    const { user } = useSelector((state) => state.Login);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });
    const [formValues, setFormValues] = useState({});
    // El registro del cliente se carga solo para el encabezado del addendum
    // (necesita el A-Number, que no viene en formClient.client).
    const { preloadersData, loadClientPreloader } = useFormPreloaders();

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            FormClients.getOneById({ id, onSuccess, onError });
        } else {
            dispatch(notifyError("No ID included in the path."));
            setFormClient(undefined);
            history.push("/forms");
        }

        return () => setFormClient(undefined);
    }, [id]);

    const onSuccess = (data) => {
        setFormClient(data);
        setIsLoading(false);
    };

    const onError = (error) => {
        dispatch(notifyError(error.message || error));
        setIsLoading(false);
        history.push(`/forms`);
    };

    useEffect(() => {
        if (formClient) {
            const { values, form } = formClient;

            const fValues = values.reduce((obj, formClientValue) => {
                const { value, formFieldMapping } = formClientValue;
                const { pdfField, type } = formFieldMapping;
                return {
                    ...obj,
                    [pdfField]: getFormattedValue(type, value),
                };
            }, {});
            setFormValues(fValues);

            // Load the PDF file
            getFileUrlAsync(form);

            if (formClient.clientId) {
                loadClientPreloader(formClient.clientId, FormClients);
            }
        }
    }, [formClient]);

    const onEditHandler = () => {
        if (formClient.status !== "completed") {
            history.push(`/forms/${id}/edit`);
        } else {
            setConfirmDialogProps((prev) => ({
                ...prev,
                open: true,
                title: "Form Completed",
                content: "This form is COMPLETED. Do you want to edit its content anyway?",
                primaryButtonText: "Yes, edit",
                onPrimaryClick: () => {
                    history.push(`/forms/${id}/edit`);
                    prev.onClose();
                },
            }));
        }
    };

    const onCancelHandler = () => history.push(`/forms`);

    const currentVersion = formClient ? findCurrentVersion(resources.forms, formClient.form) : undefined;
    const canCopyToCurrentVersion = Boolean(currentVersion) && hasPermission(user, COPY_TO_CURRENT_VERSION_PERMISSION);

    const onCopyToCurrentVersionHandler = () => history.push(`/forms/${id}/copy-to-current-version`);

    const addendumInfo = {
        formCode: formClient?.form?.code,
        clientName: formClient?.client?.fullName,
        aNumber: preloadersData.client?.alienRegistrationNo || formClient?.client?.alienRegistrationNo,
        date: new Date(),
    };

    const downloadFileNameGetter = () => {
        if (formClient)
            return `${formClient.form?.code}_${formClient.form?.version}_${formClient.client?.fullName}_${new Date().getTime()}.pdf`;

        return "form.pdf";
    };

    return (
        <Page className="view-form-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {formClient && (
                        <>
                            <div className="form-head">
                                <div className="item back">
                                    <Button secondary link small onClick={onCancelHandler}>
                                        <Icon name="arrow" size="sm" color="secondary" />
                                        <span>Back</span>
                                    </Button>
                                </div>
                                <div className="item form-status">
                                    <Status id={formClient.status} withIcon />
                                </div>
                                <div className="item">{formClient.form?.category?.category}</div>
                                <div className="item">
                                    {formClient.form?.code}
                                    <span>({formClient.form?.version})</span>
                                    {formClient.form?.isCurrent ? <Status id="active" /> : <Status id="oldVersion" />}
                                </div>
                                <div className="item">{formClient.client?.fullName}</div>
                                <div className="item form-actions">
                                    <Button secondary small onClick={onEditHandler}>
                                        <Icon name="pencil" size="md" color="white" />
                                        <span>Edit</span>
                                    </Button>
                                    {canCopyToCurrentVersion && (
                                        <Button
                                            secondary
                                            small
                                            onClick={onCopyToCurrentVersionHandler}
                                            title={`Create a new form on version ${currentVersion.version} with the values of this one`}
                                        >
                                            <Icon name="add-file" size="md" color="white" />
                                            <span>Copy to current version</span>
                                        </Button>
                                    )}
                                    <Button secondary outline small onClick={onCancelHandler}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                            <div className="form-head">
                                <div className="item">
                                    <b>Attorney:</b>
                                    <span>{getAttorneyUsersOptions().find((x) => x.id === formClient.attorneyUserId)?.name || "--"}</span>
                                </div>
                                <div className="item">
                                    <b>Case:</b>
                                    {formClient.fileCaseId ? (
                                        <span className="file-case-value">
                                            <span>{formClient.fileNo}</span>
                                            <FileCaseLink {...formClient} iconOnly />
                                        </span>
                                    ) : (
                                        <span>--</span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                    <PdfForm
                        pdfUrl={pdfUrl}
                        mode="view"
                        formValues={formValues}
                        fieldMappings={formClient?.form?.formFieldMappings}
                        downloadFileNameGetter={downloadFileNameGetter}
                        addendumInfo={addendumInfo}
                        addendums={formClient?.addendums || []}
                    />
                    <ConfirmDialog {...confirmDialogProps} />
                </>
            )}
        </Page>
    );
};

export default ViewForm;
